import { useEffect, useState } from 'react'
import { Euro, Pencil, Plus, Trash, Star, AlertCircle } from 'lucide-react'
import { Text } from '@/components/tailwind/text'
import { Button } from '@/components/tailwind/button'
import { Badge } from '@/components/tailwind/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/tailwind/table'
import { Dialog, DialogBody, DialogTitle } from '@/components/tailwind/dialog'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { MembershipFeeType } from '@/models/membership-fee'
import * as membershipFeeService from '@/services/membership-fee-services'
import { MembershipFeeForm } from '@/components/membership-fee/membership-fee-form'
import { useAdminTranslation, useCommonTranslation } from "@/i18n/hooks"
import ConfirmationDialog from '@/components/confirmation-dialog/confirmation-dialog'
import { useConfirmationDialog } from '@/components/confirmation-dialog/use-confirmation-dialog'
import { useAuth } from '@/context/use-auth'
import LoadingSpinner from "@/components/loader-spinner"
import { useToast } from '@/hooks/use-toast'
import log from '@/lib/logger'

interface SectionMembershipFeeSettingsProps {
    isMissing?: boolean;
}

export function SectionMembershipFeeSettings({ isMissing = false }: SectionMembershipFeeSettingsProps) {
    const { t } = useAdminTranslation()
    const { t: tCommon } = useCommonTranslation()
    const { token } = useAuth()
    const { toast } = useToast()
    const [feeTypes, setFeeTypes] = useState<MembershipFeeType[]>([])
    const [loading, setLoading] = useState(true)
    const [isFormOpen, setIsFormOpen] = useState(false)
    const [editingFeeType, setEditingFeeType] = useState<MembershipFeeType | null>(null)
    const [feeTypeToDelete, setFeeTypeToDelete] = useState<MembershipFeeType | null>(null)
    const confirmationDialog = useConfirmationDialog()

    useEffect(() => {
        loadFeeTypes()
    }, [token])

    async function loadFeeTypes() {
        if (!token) return
        
        try {
            setLoading(true)
            const data = await membershipFeeService.getMembershipFeeTypes(token)
            setFeeTypes(data || [])
        } catch (err: any) {
            log.error('Error loading membership fee types:', err)
            
            if (err.status === 404) {
                setFeeTypes([])
            } else {
                toast({
                    title: t('adminSettings.membershipFees.toast.loadError.title'),
                    description: t('adminSettings.membershipFees.toast.loadError.description'),
                    variant: 'destructive'
                })
            }
        } finally {
            setLoading(false)
        }
    }

    function handleCreateFeeType() {
        setEditingFeeType(null)
        setIsFormOpen(true)
    }

    function handleEditFeeType(feeType: MembershipFeeType) {
        setEditingFeeType(feeType)
        setIsFormOpen(true)
    }

    function handleDeleteFeeType(feeType: MembershipFeeType) {
        if (!feeType.canBeDeleted) {
            toast({
                title: t('adminSettings.membershipFees.toast.error.title'),
                description: t('adminSettings.membershipFees.cannotDelete'),
                variant: 'destructive'
            })
            return
        }
        
        setFeeTypeToDelete(feeType)
        confirmationDialog.openWithConfig({
            title: t('adminSettings.membershipFees.deleteConfirmation.title'),
            description: t('adminSettings.membershipFees.deleteConfirmation.message', { name: feeType.name }),
            cancelButton: {
                text: tCommon('buttons.cancel.text'),
                ariaLabel: tCommon('buttons.cancel.ariaLabel')
            },
            confirmButton: {
                text: tCommon('buttons.delete.text'),
                ariaLabel: tCommon('buttons.delete.ariaLabel'),
                color: "red"
            }
        })
    }

    async function handleSetDefault(feeType: MembershipFeeType) {
        if (!token || feeType.isDefault) return
        
        try {
            await membershipFeeService.setDefaultMembershipFeeType(token, feeType.id)
            await loadFeeTypes()
            toast({
                title: t('adminSettings.membershipFees.toast.setDefault.title'),
                description: t('adminSettings.membershipFees.toast.setDefault.description', { name: feeType.name })
            })
        } catch (err) {
            toast({
                title: t('adminSettings.membershipFees.toast.error.title'),
                description: t('adminSettings.membershipFees.toast.error.description'),
                variant: 'destructive'
            })
            log.error('Error setting default fee type:', err)
        }
    }

    async function confirmDelete() {
        if (!feeTypeToDelete || !token) return

        try {
            await membershipFeeService.deleteMembershipFeeType(token, feeTypeToDelete.id)
            await loadFeeTypes()
            setFeeTypeToDelete(null)
            toast({
                title: t('adminSettings.membershipFees.toast.deleted.title'),
                description: t('adminSettings.membershipFees.toast.deleted.description')
            })
        } catch (err) {
            toast({
                title: t('adminSettings.membershipFees.toast.error.title'),
                description: t('adminSettings.membershipFees.toast.error.description'),
                variant: 'destructive'
            })
            log.error('Error deleting fee type:', err)
        }
    }

    function handleFormSuccess() {
        setIsFormOpen(false)
        setEditingFeeType(null)
        loadFeeTypes()
    }

    function formatCurrency(amount?: number): string {
        if (amount === undefined) return '-'
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR'
        }).format(amount)
    }

    if (loading) {
        return (
            <Card>
                <CardContent className="p-8">
                    <div className="flex justify-center">
                        <LoadingSpinner />
                    </div>
                </CardContent>
            </Card>
        )
    }

    return (
        <>
            <Card>
                <CardHeader>
                    <div className="flex justify-between items-start">
                        <div>
                            <CardTitle className="flex items-center gap-2">
                                {t('adminSettings.membershipFees.title')}
                                {isMissing && (
                                    <span 
                                        title={t("adminSettings.missingConfiguration.tooltip")}
                                        aria-label={t("adminSettings.missingConfiguration.ariaLabel")}
                                    >
                                        <AlertCircle className="h-5 w-5 text-amber-500 dark:text-amber-400" />
                                    </span>
                                )}
                            </CardTitle>
                            <Text className="mt-1 text-sm text-muted-foreground">{t('adminSettings.membershipFees.description')}</Text>
                        </div>
                        <Button outline onClick={handleCreateFeeType} data-testid="add-membership-fee-button">
                            <Plus className="h-5 w-5 mr-2" data-slot="icon" />
                            {t('adminSettings.membershipFees.addNew')}
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                {feeTypes.length === 0 ? (
                    <div className="bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-400 dark:border-blue-500 p-4 rounded" data-testid="no-membership-fees-message">
                        <Text className="text-blue-700 dark:text-blue-300">
                            {t('adminSettings.membershipFees.noItems')}
                        </Text>
                    </div>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableHeader>{tCommon('fields.name')}</TableHeader>
                                <TableHeader>{tCommon('fields.description')}</TableHeader>
                                <TableHeader>{t('adminSettings.membershipFees.currentAmount')}</TableHeader>
                                <TableHeader>{tCommon('fields.status')}</TableHeader>
                                <TableHeader className="text-right">{tCommon('fields.actions')}</TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {feeTypes
                                .sort((a, b) => {
                                    // Standard entry always first
                                    if (a.isDefault) return -1
                                    if (b.isDefault) return 1
                                    // Then sort alphabetically by name
                                    return a.name.localeCompare(b.name, 'de')
                                })
                                .map((feeType) => (
                                <TableRow key={feeType.id} data-testid={`membership-fee-row-${feeType.id}`}>
                                    <TableCell className="font-medium" data-testid={`fee-name-${feeType.id}`}>
                                        {feeType.name}
                                    </TableCell>
                                    <TableCell>
                                        <Text className="text-sm">{feeType.description || '-'}</Text>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-1">
                                            <Euro className="w-4 h-4 text-zinc-500" />
                                            {formatCurrency(feeType.currentAmount)}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex gap-2">
                                            {feeType.isDefault && (
                                                <Badge color="amber" data-testid={`default-badge-${feeType.id}`}>{t('adminSettings.membershipFees.default')}</Badge>
                                            )}
                                            {feeType.currentAmount !== null && feeType.currentAmount !== undefined && (
                                                <Badge color="green" data-testid={`active-badge-${feeType.id}`}>{tCommon('fields.active')}</Badge>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex justify-end gap-2">
                                            {!feeType.isDefault && (
                                                <Button
                                                    plain
                                                    onClick={() => handleSetDefault(feeType)}
                                                    aria-label={t('adminSettings.membershipFees.setAsDefault')}
                                                    data-testid={`set-default-${feeType.id}`}
                                                >
                                                    <Star className="w-4 h-4" />
                                                </Button>
                                            )}
                                            <Button
                                                plain
                                                onClick={() => handleEditFeeType(feeType)}
                                                aria-label={t('adminSettings.membershipFees.edit')}
                                                data-testid={`edit-fee-${feeType.id}`}
                                            >
                                                <Pencil className="w-4 h-4" />
                                            </Button>
                                            {feeType.canBeDeleted && (
                                                <Button
                                                    plain
                                                    onClick={() => handleDeleteFeeType(feeType)}
                                                    aria-label={t('adminSettings.membershipFees.delete')}
                                                    data-testid={`delete-fee-${feeType.id}`}
                                                >
                                                    <Trash className="w-4 h-4 text-red-500" />
                                                </Button>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
                </CardContent>
            </Card>

            <Dialog open={isFormOpen} onClose={() => setIsFormOpen(false)}>
                <DialogTitle>
                    {editingFeeType 
                        ? t('adminSettings.membershipFees.form.title.edit') 
                        : t('adminSettings.membershipFees.form.title.create')}
                </DialogTitle>
                <DialogBody>
                    <MembershipFeeForm
                        feeType={editingFeeType}
                        onSuccess={handleFormSuccess}
                        onCancel={() => setIsFormOpen(false)}
                        hasDefaultFeeType={feeTypes.some(ft => ft.isDefault)}
                    />
                </DialogBody>
            </Dialog>

            <ConfirmationDialog
                isOpen={confirmationDialog.isOpen}
                onCancel={confirmationDialog.closeDialog}
                onConfirm={() => {
                    confirmDelete()
                    confirmationDialog.closeDialog()
                }}
                config={confirmationDialog.config!}
            />
        </>
    )
}